<template>
  <div v-on:mouseleave="clean">
    <ImageLayer
      ref="svgImage"
      v-if="element?.zone?.image"
      v-model:scale="scale"
      v-model:position="viewPosition"
      :zone="element.zone"
      :rotation-angle="element.rotation_angle"
      :mirrored="element.mirrored"
      :class="{ dragCursor: isDragged }"
      v-on:wheel="mouseAction"
      v-on:dblclick="mouseAction"
      v-on:mousedown="mouseAction"
      v-on:mouseup="mouseAction"
      v-on:mousemove="mouseAction"
    >
      <template v-slot:overlay>
        <ZoomSlider v-model:scale="scale" />
      </template>
      <template v-slot:layer>
        <!-- Visible children -->
        <ElementZone
          v-for="hlElt in displayableElements"
          :key="hlElt.id"
          :element="hlElt"
          v-on:select="elementAction(hlElt)"
          :color="elementColor(hlElt)"
        />
        <svg
          v-bind="originalBoundingBox"
          ref="svgInput"
          :viewBox="elementViewBox"
        />
        <template v-if="enabled">
          <!--
            This transparent rectangle gets shown whenever we want all mouse events to be captured by
            the ImageLayer event handlers, bypassing all the others: this includes drawing a rectangle
            or polygon, or while dragging an existing polygon around.
          -->
          <rect
            v-if="captureCursor"
            v-bind="originalBoundingBox"
            fill="transparent"
          />
          <!-- Display the currently edited element using lines and circles -->
          <g
            v-if="selectedElement?.zone"
            v-on:mouseover="hoveredId = selectedElement.id"
            v-on:mouseleave="hoveredId = null"
          >
            <!-- A polyline for the background color, allowing to drag the element around. -->
            <polyline
              :points="svgPolygon(selectedElement.zone.polygon)"
              :fill="DRAWN_POLYGON_COLOR"
              fill-opacity="0.5"
              class="mouseCursor"
            />
            <!-- Draw lines separately from the polyline; this might later allow dragging lines or creating median points -->
            <g
              v-for="(point, index) in selectedElement.zone.polygon.slice(0, -1)"
              :key="'line-' + index"
            >
              <!-- Hidden line to make hovering a polygon line easier -->
              <line
                v-if="tool === 'median'"
                :x1="point[0]"
                :y1="point[1]"
                :x2="selectedElement.zone.polygon[index + 1][0]"
                :y2="selectedElement.zone.polygon[index + 1][1]"
                :stroke="DRAWN_POLYGON_COLOR"
                v-on:mouseover="medianPointHover(index)"
                v-on:mouseleave="hoveredLine = null"
                v-on:mouseup.left="addMedianPoint(index)"
                stroke-opacity="0"
                stroke-width="10"
                vector-effect="non-scaling-stroke"
                :class="tool === 'median' ? 'medianCursor' : 'mouseCursor'"
              />
              <line
                :x1="point[0]"
                :y1="point[1]"
                :x2="selectedElement.zone.polygon[index + 1][0]"
                :y2="selectedElement.zone.polygon[index + 1][1]"
                :stroke="DRAWN_POLYGON_COLOR"
                :stroke-width="strokeWidth(index)"
                vector-effect="non-scaling-stroke"
                v-on:mouseover="medianPointHover(index)"
                v-on:mouseleave="hoveredLine = null"
                v-on:mouseup.left="addMedianPoint(index)"
                :class="tool === 'median' ? 'medianCursor' : 'mouseCursor'"
              />
              <!-- Preview a point to be added with the median point tool -->
              <line
                class="previewPoint"
                v-if="tool === 'median' && newMedianPoint"
                :x1="newMedianPoint[0]"
                :y1="newMedianPoint[1]"
                :x2="newMedianPoint[0]"
                :y2="newMedianPoint[1]"
                stroke="orange"
                :stroke-width="SMALLER_POINT_DIAMETER"
                stroke-linecap="round"
                vector-effect="non-scaling-stroke"
              />
            </g>
            <!--
              The non-scaling-stroke effect causes the points to keep the same size, no matter the zoom level.
              This is why we use a line here and not a circle; only the stroke can keep its size, not a whole shape.
              A circle's radius would be relative to the element, and re-computing its radius each time
              to fit the whole screen is too complex.
              The line starts and ends at the same point, so all we see is one round stroke linecap.

              We only map the mouse up and down event on this point; the move or wheel events will be captured
              by the ImageLayer. We also do not draw the last point, since it is the same as the first.
            -->
            <line
              v-for="(point, index) in selectedElement.zone.polygon.slice(0, -1)"
              :key="'point-' + index"
              :x1="point[0]"
              :y1="point[1]"
              :x2="point[0]"
              :y2="point[1]"
              :stroke="DRAWN_POLYGON_COLOR"
              :stroke-width="pointDiameter"
              stroke-linecap="round"
              vector-effect="non-scaling-stroke"
              v-on:mousedown="pointMouseDown(index)"
              v-on:mouseup="(event) => pointMouseUp(event, index)"
              :class="{ mouseCursor: tool === 'select' || (tool === 'polygon' && index === 0) }"
            />
          </g>
        </template>
      </template>
    </ImageLayer>
    <CreationForm
      v-if="createModal"
      :element="element"
      v-model="createModal"
    />
    <DeleteModal
      v-if="selectedElement && tool === 'deletion'"
      :target="selectedElement"
      :model-value="true"
    />
    <EditModal
      v-if="editModal && selectedElement"
      :element="selectedElement"
      v-model="editModal"
    />
  </div>
</template>

<script lang="ts">
import { cloneDeep } from 'lodash'
import Mousetrap from 'mousetrap'
import { mapActions, mapState, mapStores, mapWritableState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import CreationForm from '@/components/Element/CreationForm.vue'
import EditModal from '@/components/Element/EditionForm.vue'
import DeleteModal from '@/components/SingleDeletionModal.vue'
import { DEFAULT_POLYGON_COLOR, DRAWN_POLYGON_COLOR, ZOOM_FACTORS } from '@/config'
import {
  type BoundingBox,
  InvalidPolygonError,
  boundingBox,
  checkPolygon,
  errorParser,
  medianPoint,
  mirrorX,
  pointsEqual,
  polygonsEqual,
  rotateAround,
  shiftPolygon,
  svgPolygon,
} from '@/helpers'
import { corporaMixin } from '@/mixins'
import {
  useAnnotationStore,
  useAuthStore,
  useCorporaStore,
  useElementStore,
  useNotificationStore,
} from '@/stores'
import type { Element, ElementBase, Point, Polygon, UUID, Zone } from '@/types'

import ElementZone from './ElementZone.vue'
import ImageLayer, { type ViewPosition } from './ImageLayer.vue'
import ZoomSlider, { type ScaleState } from './ZoomSlider.vue'

/**
 * Names returned by MouseEvent.type depending on what mouse event occurred
 */
type MouseEventType = 'dblclick' | 'mouseup' | 'mousedown' | 'mousemove' | 'wheel'

export type ElementWithZone = (Element | ElementBase) & { zone: Zone }

function hasZone(element: Element | ElementBase | null | undefined): element is ElementWithZone {
  return !!element?.zone
}

export default defineComponent({
  /**
   * This component allows to visualize and edit
   * sub-elements on a given element image
   */
  components: {
    CreationForm,
    ImageLayer,
    ElementZone,
    ZoomSlider,
    DeleteModal,
    EditModal,
  },
  mixins: [corporaMixin],
  props: {
    element: {
      type: Object as PropType<ElementWithZone>,
      required: true,
    },
  },
  data: () => ({
    POINT_DIAMETER: '15px',
    // For when points are displayed but can't be edited / dragged
    SMALLER_POINT_DIAMETER: '8px',
    DRAWN_POLYGON_COLOR,
    DEFAULT_POLYGON_COLOR,
    createModal: false,
    editModal: false,
    loading: false,
    // Stores an edited polygon point index
    movedPointIndex: null as number | null,
    // Mouse is kept down
    mouseDown: false,
    // Required to compute a relative mouse translation
    lastMousePosition: null as Point | null,
    // Set to true when the edited polygon has been modified (dragged or distorted)
    updatedPolygon: false,
    /**
     * When an annotation action is cancelled and the mouse was held down,
     * this is set to true to cause the next mouseup event to be ignored.
     * Note that this mouseup can occur anywhere, including outside of the component.
     */
    cancelled: false,
    /**
     * View scaling
     * Factor represents the zoom percentage index from ZOOM_FACTORS array
     */
    scale: { x: 0, y: 0, factor: 0, applied: true } as ScaleState,
    // Image position
    viewPosition: { x: 0, y: 0, applied: false, recenter: true } as ViewPosition,
    /**
     * Hovered line in a selected polygon when using the median point tool
     * This is the index of the first of the two points of the line in the selected element's polygon.
     */
    hoveredLine: null as number | null,
  }),
  beforeUnmount() {
    this.annotationStore.cleanVisible(this.element.id)
    if (this.selectedElement) this.selectedElement = null
  },
  computed: {
    ...mapState(useAnnotationStore, [
      'enabled',
      'tool',
      'batchCreation',
      'batchDeletion',
      'batchTypeEdition',
      'visible',
      'defaultType',
      'defaultName',
      'batchCreateAvailable',
    ]),
    ...mapWritableState(useAnnotationStore, ['selectedElement', 'hoveredId']),
    ...mapState(useCorporaStore, ['corpora']),
    ...mapStores(useAnnotationStore, useElementStore),
    ...mapState(useAuthStore, ['isVerified']),
    /**
     * Bounding box of the polygon, ignoring rotation and mirroring.
     */
    originalBoundingBox(): BoundingBox | null {
      if (!this.element?.zone?.polygon) return null
      return boundingBox(this.element.zone)
    },
    /**
     * Center point of the element's bounding box, used to apply both mirroring and rotation.
     * Uses originalBoundingBox to allow it to be used in zoneBBox for the final bounding box computation
     */
    center(): Point {
      if (!this.originalBoundingBox) throw new Error('Missing bounding box')
      return [
        Math.floor(this.originalBoundingBox.x + this.originalBoundingBox.width / 2),
        Math.floor(this.originalBoundingBox.y + this.originalBoundingBox.height / 2),
      ]
    },
    /**
     * The zone's bounding box after rotation. This potentially exceeds the image's bounds or has negative coordinates.
     */
    zoneBBox(): BoundingBox {
      if (!this.element?.zone) throw new Error('Missing element zone')
      return boundingBox(
        {
          polygon: this.element.zone.polygon.map((point) =>
            rotateAround(point, this.center, this.element.rotation_angle),
          ),
        },
        { imageBounds: false },
      )
    },
    elementViewBox(): string {
      const { x, y, width, height } = this.zoneBBox
      return [x, y, width, height].join(' ')
    },
    /**
     * Whether or not to enable an SVG layer to capture the mouse cursor's position while drawing
     */
    captureCursor(): boolean {
      return (
        this.tool === 'polygon' ||
        // Moving a point or dragging the polygon in edition mode
        (this.tool === 'select' && this.selectedElement !== null && this.mouseDown)
      )
    },
    /**
     * UUIDs of highlighted elements on this element
     */
    hlIds(): Set<UUID> {
      return this.visible[this.element.id] || new Set()
    },
    /**
     * Whether or not the selected element is displayable
     */
    displaySelectedElt(): boolean {
      return (
        // There is a selected element and it has a zone
        !!this.selectedElement?.zone &&
        // Element is not being created
        this.selectedElement.id !== 'created-polygon' &&
        // Element should not be already visible
        !this.hlIds.has(this.selectedElement.id)
      )
    },
    displayableElements(): ElementWithZone[] {
      // Returns displayable sub-elements e.g. visible or selected
      const elements = Array.from(this.hlIds)
        .map((id) => this.elementStore.elements[id])
        .filter((e): e is ElementWithZone => !!e?.zone)
      // Append a selected element in order to display it
      if (hasZone(this.selectedElement) && this.displaySelectedElt)
        elements.push(this.selectedElement)
      return elements
    },
    isDragged(): boolean {
      // Display a drag cursor when mouse is kept down except while drawing
      return this.mouseDown && (!this.enabled || this.tool === 'select')
    },
    corpusId(): UUID | null {
      // Required for corporaMixin
      return this.element.corpus?.id ?? null
    },
    /**
     * Diameter of an SVG circle representing a point, as a CSS size with a unit
     */
    pointDiameter(): string {
      if (this.tool === 'median') return this.SMALLER_POINT_DIAMETER
      return this.POINT_DIAMETER
    },
    /**
     * New median point to be created on the currently hovered line
     */
    newMedianPoint(): Point | null {
      if (!this.selectedElement?.zone || this.hoveredLine === null) return null
      const point = this.selectedElement.zone.polygon.slice(0, -1)[this.hoveredLine]
      return this.findMedianPoint(this.hoveredLine, point)
    },
  },
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    svgPolygon,
    clean() {
      if (!this.mouseDown) return
      // Clean editor when going outside component
      this.mouseDown = false
      this.lastMousePosition = null
      // Trigger a view update translating the image
      if (!this.viewPosition.recenter) this.viewPosition = { ...this.viewPosition, recenter: true }
      // Reset the edited polygon during an edition
      if (!this.selectedElement?.id) return
      const baseElt = this.elementStore.elements[this.selectedElement.id]
      if (baseElt) this.select(baseElt)
    },

    /**
     * Limit a position on the image to not exceed the element's bounds
     * @param position Original position.
     */
    elementLimits(position: Point): Point {
      if (!this.originalBoundingBox) return position
      const { x, y, width, height } = this.originalBoundingBox
      let [posX, posY] = position
      if (posX < x) posX = x
      else if (posX > x + width) posX = x + width
      if (posY < y) posY = y
      else if (posY > y + height) posY = y + height
      return [posX, posY]
    },

    /**
     * Returns relative mouse position on a scaled SVG layer
     */
    getPosition(event: MouseEvent): DOMPoint {
      const svgPoint = (this.$refs.svgInput as SVGSVGElement).createSVGPoint()
      Object.assign(svgPoint, { x: event.clientX, y: event.clientY })
      return svgPoint.matrixTransform(
        (this.$refs.svgInput as SVGSVGElement).getScreenCTM()!.inverse(),
      )
    },

    mouseAction(e: MouseEvent) {
      // Ignore unsupported mouse event types
      if (!['dblclick', 'mouseup', 'mousedown', 'mousemove', 'wheel'].includes(e.type)) return
      const eventType = e.type as MouseEventType

      // Ignore everything but the left mouse button
      if (eventType === 'mousedown' && e.buttons !== 1) return

      // Nothing to do on a simple mouse move (no dragging)
      if (eventType === 'mousemove' && !this.mouseDown && !this.selectedElement) return

      const position = this.getPosition(e)
      if (!position) throw new Error('Mouse position could not be determined on SVG layer.')
      const mouseCoords: Point = [position.x, position.y]

      if (['mouseup', 'mousedown'].includes(eventType)) {
        this.mouseDown = eventType === 'mousedown'

        /*
         * When an annotation is cancelled while the user holds the mouse button down,
         * cancelled is set to ignore the next mouseup event.
         * The mouseup event can however occur outside of this component, so it will never
         * be captured here; so if we catch a mousedown event instead, we will also
         * turn off the cancellation flag, without ignoring the event.
         */
        if (this.cancelled) {
          this.cancelled = false
          if (!this.mouseDown) return
        }

        /*
         * Remove focus from anything outside of the InteractiveImage:
         * we cannot directly focus on the SVG, but we need to unfocus any input, textarea, select
         * to make sure that keyboard shortcuts will not be ignored by Mousetrap.
         */
        document.documentElement.focus()
      }

      // Update the viewBox on mouseup event if the image has been translated
      if (
        (this.viewPosition.x || this.viewPosition.y) &&
        !this.viewPosition.recenter &&
        eventType === 'mouseup'
      ) {
        this.viewPosition = { ...this.viewPosition, recenter: true }
        this.lastMousePosition = null
        return
      }

      // Dispatch actions
      if (eventType === 'wheel') {
        this.zoom(mouseCoords, e as WheelEvent)
      } else if (!this.enabled || e.shiftKey) {
        if (this.mouseDown) this.translate(mouseCoords)
      } else if (this.tool === 'polygon') {
        this.editPolygon(mouseCoords, eventType)
      } else if (this.tool === 'rectangle') {
        this.editRectangle(mouseCoords, eventType)
      } else if (this.tool === 'select') {
        this.editSelected(mouseCoords, eventType)
      }

      // Set last move position
      if (this.mouseDown && eventType === 'mousemove') {
        if (this.viewPosition.applied) {
          // Do not store mouse coords right after the CSS translation
          this.lastMousePosition = mouseCoords
        }
        this.viewPosition.applied = true
      } else {
        this.lastMousePosition = null
      }
    },

    translate([x, y]: Point) {
      if (!this.lastMousePosition) return
      const [lastX, lastY] = this.lastMousePosition
      const [dX, dY] = [x - lastX, y - lastY]
      this.viewPosition = {
        x: this.viewPosition.x + dX,
        y: this.viewPosition.y + dY,
        recenter: false,
        applied: false,
      }
    },

    zoom(position: Point, event: WheelEvent) {
      if (this.mouseDown) return
      let factor = this.scale.factor + (event.deltaY < 0 ? 1 : -1)
      // Limit zoom factor
      if (factor < 0) factor = 0
      if (factor >= ZOOM_FACTORS.length) factor = ZOOM_FACTORS.length - 1
      // Factor has not been updated
      if (factor === this.scale.factor) return
      let [x, y] = this.elementLimits(position)
      if (this.element.mirrored) [x, y] = mirrorX([x, y], this.center)
      if (this.element.rotation_angle)
        [x, y] = rotateAround([x, y], this.center, this.element.rotation_angle)
      if (!this.scale.applied) this.scale = { ...this.scale, factor }
      else this.scale = { x, y, factor, applied: false }
    },

    async create() {
      if (!this.selectedElement?.zone) return
      // Check polygon and open element creation modal
      try {
        this.selectedElement.zone.polygon = checkPolygon(this.selectedElement.zone.polygon)
      } catch (e) {
        if (!(e instanceof InvalidPolygonError)) throw e
        this.notify({ type: 'error', text: e.message })
        this.selectedElement = null
        return
      }

      // Open the modal when batch annotation is not available on this corpus.
      if (!this.element.corpus || !this.batchCreateAvailable(this.element.corpus.id)) {
        this.createModal = true
        return
      }

      try {
        await this.annotationStore.create({
          corpus: this.element.corpus.id,
          parent: this.element.id,
          polygon: this.selectedElement.zone.polygon,
        })
        this.notify({ type: 'success', text: 'Element created.' })
      } catch (e) {
        this.notify({
          type: 'error',
          text: `An error occurred during element creation: ${errorParser(e)}`,
        })
      }
    },

    async editType(elt: Element | ElementBase) {
      if (this.loading) return
      if (!this.canEdit(elt)) {
        this.notify({
          type: 'error',
          text: "You don't have the necessary rights to edit this element's type.",
        })
        return
      }

      if (this.batchTypeEdition && this.corpusId && this.defaultType[this.corpusId]) {
        // Only trigger the typeEdit action when in batch edition mode and a type is selected
        this.loading = true
        try {
          await this.annotationStore.typeEdit(elt)
        } catch (e) {
          this.notify({ type: 'error', text: `${errorParser(e)}` })
        } finally {
          this.loading = false
        }
      } else {
        this.selectedElement = elt
        this.editModal = true
      }
    },

    select(elt: Element | ElementBase) {
      // Do not select an element after translating the image
      if (this.viewPosition.x || this.viewPosition.y) return
      // Allow to unselect an element
      if (!elt || (this.selectedElement && elt.id === this.selectedElement.id)) {
        this.selectedElement = null
        return
      }
      if (!elt.zone) throw new Error('Cannot select an element without a zone')
      this.selectedElement = {
        ...elt,
        zone: {
          ...elt.zone,
          image: { ...elt.zone.image },
          polygon: [...elt.zone.polygon],
        },
      }
      if (this.$route.query.highlight !== elt.id) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            highlight: elt.id,
          },
        })
      }
    },

    /**
     * Called when an ElementZone fires a select event.
     * This comes from a mouseup event, so this should be ignored after a cancellation.
     */
    elementSelect(elt: Element | ElementBase) {
      if (this.cancelled) {
        this.cancelled = false
        return
      }
      this.select(elt)
    },

    elementAction(elt: Element | ElementBase) {
      if (this.enabled && this.batchDeletion) this.elementDelete(elt)
      else if (this.enabled && this.tool === 'type-edit') this.editType(elt)
      else this.elementSelect(elt)
    },

    editPolygon(position: Point, action: MouseEventType) {
      const polygon = this.selectedElement?.zone && [...this.selectedElement.zone.polygon]
      const point = this.elementLimits(position)
      if (action === 'mouseup') {
        // Create a temporary edited element with drawn polygon first and last point
        if (!polygon) {
          this.selectedElement = {
            id: 'created-polygon',
            name: '',
            corpus: this.element.corpus,
            type: '',
            zone: {
              ...this.element.zone,
              image: { ...this.element.zone.image },
              polygon: [point, point],
            },
            thumbnail_url: null,
            rotation_angle: this.element.rotation_angle,
            mirrored: this.element.mirrored,
          }
          return
        }
        polygon.push(point)
      } else if (polygon && this.selectedElement && action === 'mousemove') {
        // Add a segment between the last polygon point and the current cursor position
        polygon.splice(-1, 1, point)
      } else if (polygon && this.selectedElement && action === 'dblclick') {
        /*
         * Remove the above segment, which was only there only to provide feedback to the user,
         * and replace it with the first point of the polygon to close it.
         */
        polygon.splice(-1, 1, polygon[0])
      }

      // Update the edited element
      if (polygon && hasZone(this.selectedElement)) this.selectedElement.zone.polygon = polygon

      // Trigger element creation on double click, only after updating the selected element
      if (this.selectedElement && action === 'dblclick') this.create()
    },

    editRectangle(position: Point, action: MouseEventType) {
      const polygon = hasZone(this.selectedElement)
        ? cloneDeep(this.selectedElement.zone.polygon)
        : null
      if (!polygon && action === 'mousedown') {
        // Create a temporary edited element with all rectangle points
        this.selectedElement = {
          id: 'created-polygon',
          name: '',
          corpus: this.element.corpus,
          type: '',
          zone: {
            ...this.element.zone,
            polygon: Array.from({ length: 5 }, () => [...position]),
          },
          thumbnail_url: null,
          rotation_angle: this.element.rotation_angle,
          mirrored: this.element.mirrored,
        }
      } else if (this.selectedElement && polygon && action === 'mousemove') {
        // Move rectangle points depending on mouse pointer position
        const limitedPosition = this.elementLimits(position)
        polygon.splice(2, 1, limitedPosition)
        // Second and third points respectively receive x and y cursor coordinates
        polygon[1][0] = limitedPosition[0]
        polygon[3][1] = limitedPosition[1]
      } else if (this.selectedElement && action === 'mouseup') {
        return this.create()
      }
      if (polygon && hasZone(this.selectedElement)) this.selectedElement.zone.polygon = polygon
    },

    /**
     * Display thicker lines when hovering using the median point tool.
     * @param {number} index Index of the hovered line in the polygon.
     */
    strokeWidth(index: number): number {
      if (this.tool === 'median' && this.hoveredLine === index) return 5
      return 2
    },

    /**
     * When a polygon line is hovered, set hoveredLine to its index.
     */
    medianPointHover(index: number) {
      if (this.tool === 'median') {
        this.hoveredLine = index
      }
    },

    /**
     * Calculates a polygon line's median point.
     * Used to preview it as the line is hovered, then to insert it into the polygon.
     */
    findMedianPoint(index: number, point: Point): Point {
      return medianPoint(index, point, this.selectedElement?.zone?.polygon ?? [])
    },

    /**
     * Called when clicking on a polygon line using the median point tool.
     * Inserts that line's median point into the element polygon.
     */
    addMedianPoint(index: number) {
      if (this.tool !== 'median' || !this.newMedianPoint) return
      const polygon: Polygon = hasZone(this.selectedElement)
        ? [...this.selectedElement.zone.polygon]
        : []

      // Insert the new point
      polygon.splice(index + 1, 0, this.newMedianPoint)

      // Update the edited element
      if (polygon) this.selectedElement!.zone!.polygon = polygon
      this.updatedPolygon = true

      // Save new polygon
      this.saveSelected()
    },

    /**
     * Event handler for the mousedown event on a single point.
     *
     * With the selection tool, this sets movedPointIndex to begin moving a point.
     * With any other tool, we do nothing and let the ImageLayer's mouseup event deal with it.
     *
     * @param index Zero-based index of the point in the polygon.
     */
    pointMouseDown(index: number) {
      if (this.tool === 'select') this.movedPointIndex = index
    },

    /**
     * Event handler for the mouseup event on a single point.
     *
     * With the polygon tool, a click on the first point should cause the polygon to be closed and created;
     * a click on any other point should do nothing.
     * With any other tool, we do nothing and let the ImageLayer's mouseup event deal with it.
     *
     * @param event Mouse event instance.
     * @param index Zero-based index of the point in the polygon.
     */
    pointMouseUp(event: MouseEvent, index: number) {
      // When an annotation action is cancelled, we should ignore the next mouseup event.
      if (this.cancelled) {
        this.cancelled = false
        return
      }
      if (this.tool !== 'polygon' || !this.selectedElement?.zone) return
      /*
       * Ensure clicking on any point with a polygon tool does not cause the ImageLayer's mouseup event to be fired
       * This prevents creating a new polygon right after finishing one.
       */
      event.stopPropagation()
      /*
       * Stopping event propagation means the `mouseDown` property may not be updated properly,
       * so we ensure this is set to `false` again to signal that the mouse buttons have been released.
       */
      this.mouseDown = false
      if (index !== 0) return
      /*
       * When the user moves the mouse around, editPolygon adds a fake point at the end of the polygon;
       * we therefore replace that final point with the first point to properly close the polygon.
       * Merely adding the first point with polygon.push(polygon[0]) would cause an extra point to be
       * present when the element is created.
       */
      const polygon = [...this.selectedElement.zone.polygon]
      polygon.splice(-1, 1, polygon[0])
      this.selectedElement.zone.polygon = polygon

      // Trigger element creation
      this.create()
    },

    /**
     * Called by the generic editSelected event handler to handle a mouse move while a point is being dragged.
     * This is not an event handler by itself because this move event cannot be captured on the SVG element of a point,
     * since the mouse might end up moving outside of the point to drag it around.
     * Instead, the original event is captured by the ImageLayer event handlers due to the captureCursor rectangle.
     * @param position X,Y coordinates of the mouse on the element.
     * @returns An updated polygon.
     */
    movePoint(position: Point): Polygon | null {
      if (!hasZone(this.selectedElement) || this.movedPointIndex === null) return null
      const polygon = [...this.selectedElement.zone.polygon]
      const limitedPosition = this.elementLimits(position)

      // Do nothing if the polygon was not updated
      if (pointsEqual(polygon[this.movedPointIndex], limitedPosition)) return null

      polygon.splice(this.movedPointIndex, 1, limitedPosition)

      // The first and last points of a polygon are the same; when editing the first point, also edit the last one.
      if (this.movedPointIndex === 0) polygon.splice(-1, 1, limitedPosition)

      return polygon
    },

    /**
     * Called by the generic editSelected event handler to handle a mouse move while the SVG polyline is being dragged.
     * This is not an event handler by itself because this move event cannot be captured on the polyline,
     * since the mouse might end up moving outside of it to drag it around.
     * @param X,Y coordinates of the mouse on the element.
     * @returns An updated polygon.
     */
    movePolygon(position: Point): Polygon | null {
      // Ignore any potential 0-pixel moves
      if (
        !this.lastMousePosition ||
        !hasZone(this.selectedElement) ||
        pointsEqual(this.lastMousePosition, position)
      )
        return null

      const polygon = this.selectedElement.zone.polygon

      // Compute mouse translation vector relatively to image size
      const [lastX, lastY] = this.lastMousePosition
      const [x, y] = position
      const newPolygon = shiftPolygon(polygon, [x - lastX, y - lastY])

      // If the translation results in any of the points being out of bounds, abort
      if (!newPolygon.every((point) => pointsEqual(point, this.elementLimits(point)))) return null
      return newPolygon
    },

    /**
     * Save the selected element once an update is completed.
     */
    async saveSelected() {
      if (!hasZone(this.selectedElement)) throw new Error('No selected element with zone to save')
      // Reset edition data
      this.movedPointIndex = null
      if (this.lastMousePosition) {
        // Hover edited element again: the captureCursor layer caused the selected element to no longer be hovered.
        this.hoveredId = this.selectedElement.id
      }
      const baseElt = this.elementStore.elements[this.selectedElement.id]
      // Do not save an unchanged polygon
      if (baseElt && !this.updatedPolygon) return

      this.updatedPolygon = false
      try {
        const reorderedPolygon = checkPolygon(this.selectedElement.zone.polygon)

        // The polygon was unchanged, no patch required!
        if (baseElt.zone && polygonsEqual(reorderedPolygon, baseElt.zone.polygon)) return

        await this.elementStore.patch(this.selectedElement.id, { polygon: reorderedPolygon })

        this.notify({ type: 'success', text: 'Element polygon updated.' })
      } catch (e) {
        // Cancel polygon update in case of error
        this.select(baseElt || null)
        this.notify({
          type: 'error',
          text: `An error occurred while updating the element: ${errorParser(e)}`,
        })
        // throw e
      }
    },

    /**
     * Global mouse event handler for the selection tool.
     * @param position X,Y coordinates of the mouse on the element.
     * @param action Mouse event name.
     */
    editSelected(position: [x: number, y: number], action: MouseEventType) {
      if (action === 'mousedown') {
        if (this.hoveredId && this.elementStore.elements[this.hoveredId])
          this.selectedElement = cloneDeep(this.elementStore.elements[this.hoveredId])
        else this.selectedElement = null
        return
      }

      if (action === 'mouseup') {
        if (this.selectedElement) this.saveSelected()
        else this.translate(position)
        return
      }

      if (action !== 'mousemove' || !this.mouseDown) return

      // No selected element: the whole image should be dragged
      if (!this.selectedElement) {
        this.translate(position)
        return
      }

      // Defer to the point- or polygon-specific move methods to handle the actual moving
      let polygon = null
      if (this.movedPointIndex !== null) {
        polygon = this.movePoint(position)
      } else if (this.lastMousePosition) {
        polygon = this.movePolygon(position)
      }

      // An API request should be made to update the polygon once the move finishes.
      this.updatedPolygon = true

      // If we got a new polygon, update the element in the store
      if (polygon !== null) this.selectedElement!.zone!.polygon = polygon
    },

    toggleKeyboardShortcuts(value: boolean) {
      /*
       * Use nextTick to wait until modals close and unbind their keys before binding ours,
       * or to wait for the component to be fully ready so `this.abort` is available
       */
      if (value) this.$nextTick(() => Mousetrap.bind('esc', this.abort))
      else Mousetrap.unbind('esc')
    },

    /**
     * Stop any ongoing edition action: drawing a new polygon, moving one, or moving a point.
     */
    abort() {
      if (
        this.tool === 'select' &&
        this.mouseDown &&
        (this.movedPointIndex !== null || this.lastMousePosition)
      ) {
        /*
         * Moving a point or an existing polygon: reset the selected element to the backend data and reset the mouse data
         * If the element cannot be found for any reason, just deselect entirely.
         */
        if (this.selectedElement)
          this.selectedElement = this.elementStore.elements[this.selectedElement.id] ?? null
        this.mouseDown = false
        this.movedPointIndex = null
        this.lastMousePosition = null
        this.updatedPolygon = false
        this.cancelled = true
      } else {
        // Drawing a new polygon: erase the selected element to delete it completely.
        this.selectedElement = null
      }
    },
    canEdit(element: Element | ElementBase): boolean {
      return (
        this.corpus !== null &&
        this.isVerified &&
        this.canWrite(this.corpus) &&
        (!('rights' in element) || this.elementStore.canWrite(element.id))
      )
    },
    /**
     * Allow deleting elements only if the user is verified and has admin rights on the corpus.
     * Additionally, if the element has a `rights` attribute, look for an admin right.
     * This element rights check cannot be done for every element since the rights attribute is not available in all endpoints.
     */
    canDelete(element: Element | ElementBase): boolean {
      return (
        this.corpus !== null &&
        this.isVerified &&
        this.canAdmin(this.corpus) &&
        (!('rights' in element) || this.elementStore.canWrite(element.id))
      )
    },
    async elementDelete(elt: Element | ElementBase) {
      if (this.loading) return
      if (!this.canDelete(elt)) {
        this.notify({
          type: 'error',
          text: "You don't have the necessary rights to delete this element.",
        })
        return
      }
      this.loading = true
      try {
        await this.elementStore.delete(elt.id)
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.loading = false
      }
    },

    elementColor(element: Element | ElementBase): string {
      const elementColor = this.getType(element.type)?.color
      if (elementColor) return `#${elementColor}`
      else return DEFAULT_POLYGON_COLOR
    },

    onBatchSwitch(newValue: boolean, oldValue: boolean) {
      // When switching to a batch mode, unselect the currently selected element if any
      if (!oldValue && newValue && this.selectedElement) this.selectedElement = null
    },
  },
  watch: {
    enabled: {
      handler: 'toggleKeyboardShortcuts',
      immediate: true,
    },
    createModal(newValue: boolean) {
      /*
       * When the creation modal closes, erase the selected element
       * and reset the keyboard shortcuts, since the modal will remap the Esc key itself.
       */
      if (!newValue) {
        this.selectedElement = null
        this.toggleKeyboardShortcuts(true)
      }
    },
    element(newValue, oldValue) {
      if (newValue?.id === oldValue?.id) return
      // The element changed but the component was not re-mounted; reset the zoom and position
      this.scale = { x: 0, y: 0, factor: 0, applied: true }
      this.viewPosition = { x: 0, y: 0, applied: true, recenter: false }
    },
    batchCreation: 'onBatchSwitch',
    batchTypeEdition: 'onBatchSwitch',
    batchDeletion: 'onBatchSwitch',
  },
})
</script>

<style scoped lang="scss">
.flex-column {
  flex-direction: column;
}
.mouseCursor:hover {
  cursor: pointer;
}
.medianCursor:hover {
  cursor: crosshair;
}
.dragCursor {
  cursor: grabbing;
}
.previewPoint {
  pointer-events: none;
}
</style>
